import { type MouseEvent } from 'react';
import iconForms from '@/assets/product-icons/icon-forms.svg';
import iconFacilities from '@/assets/product-icons/icon-facilities.svg';
import iconMinistries from '@/assets/product-icons/icon-ministries.svg';
import iconAdoration from '@/assets/product-icons/icon-adoration.svg';
import iconMessages from '@/assets/product-icons/icon-messages.svg';
import iconMassIntentions from '@/assets/product-icons/icon-intentions.svg';
import iconDiscoverMass from '@/assets/product-icons/icon-discoverMass.svg';
import iconLibraryOfArt from '@/assets/product-icons/icon-libraryOfArt.svg';
import iconBulletinResources from '@/assets/product-icons/icon-bulletinResources.svg';
import iconMPA from '@/assets/product-icons/icon-mpa.svg';
import iconWebsites from '@/assets/product-icons/icon-websites.svg';
import iconCamino from '@/assets/product-icons/icon-camino.svg';
import iconBrandingAndDesign from '@/assets/product-icons/icon-branding.svg';
import iconDiocesanPhotography from '@/assets/product-icons/icon-diocesanPhotography.svg';
import { Image, Skeleton } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import styles from './ProductsLinks.module.sass';
import { QUERY_KEYS } from '@/constants';
import {
  type ChurchProductsMap,
  getProductInternalRoute,
  hasCommunityListScopeType,
  hasGroupScopeType,
  isLimitedAdminScope,
} from '@/helpers';
import { useQuery } from '@tanstack/react-query';
import churchesService from '@/services/churches.service';
import {
  useGlobalSettings,
  useImpersonation,
  useIsLimitedClusterSubView,
  useSetAdminProductContextMutation,
} from '@/hooks';
import { paletteMinistry } from '@/styles/palette';
import { CHURCH_PRODUCT } from '@/types';

const LOADING_SKELETON_COUNT = 8;

type ProductLink = {
  label: string;
  icon: string;
  id: CHURCH_PRODUCT;
  bg: string;
};

const LINKS: ProductLink[] = [
  {
    id: CHURCH_PRODUCT.MPA,
    label: 'myParish',
    icon: iconMPA,
    bg: '#1ba2ea',
  },
  {
    id: CHURCH_PRODUCT.MESSAGES,
    label: 'Messages',
    icon: iconMessages,
    bg: '#89c9ea',
  },
  {
    id: CHURCH_PRODUCT.CAMINO,
    label: 'Camino',
    icon: iconCamino,
    bg: '#792CC0',
  },
  {
    id: CHURCH_PRODUCT.FORMS,
    label: 'Forms',
    icon: iconForms,
    bg: '#70a9ee',
  },
  {
    id: CHURCH_PRODUCT.DISCOVER_MASS,
    label: 'Discover Mass',
    icon: iconDiscoverMass,
    bg: '#f28521',
  },
  {
    id: CHURCH_PRODUCT.BULLETIN_RESOURCES,
    label: 'Bulletin Resources',
    icon: iconBulletinResources,
    bg: '#d94f75',
  },
  {
    id: CHURCH_PRODUCT.LIBRARY_OF_ART,
    label: 'Library of Art',
    icon: iconLibraryOfArt,
    bg: '#e2a730',
  },
  {
    id: CHURCH_PRODUCT.BRANDING_AND_DESIGN,
    label: 'Branding and Design',
    icon: iconBrandingAndDesign,
    bg: '#9bbc61',
  },
  {
    id: CHURCH_PRODUCT.DIOCESAN_PHOTOGRAPHY,
    label: 'Diocesan Photography',
    icon: iconDiocesanPhotography,
    bg: '#43bfb9',
  },
  {
    id: CHURCH_PRODUCT.MY_PARISH_FACILITIES,
    label: 'myParish Facilities',
    icon: iconFacilities,
    bg: '#432E9D',
  },
  {
    id: CHURCH_PRODUCT.MY_PARISH_MINISTRIES,
    label: 'myParish Ministries',
    icon: iconMinistries,
    bg: paletteMinistry[5],
  },
  {
    id: CHURCH_PRODUCT.MY_PARISH_ADORATION,
    label: 'myParish Adoration',
    icon: iconAdoration,
    bg: '#E766B9',
  },
  {
    id: CHURCH_PRODUCT.MASS_INTENTIONS,
    label: 'Mass Intentions',
    icon: iconMassIntentions,
    bg: '#FC6142',
  },
  {
    id: CHURCH_PRODUCT.WEBSITES,
    label: 'Websites',
    icon: iconWebsites,
    bg: '#2c7dc6',
  },
];

type ProductsLinksProps = {
  onProductSelect?: () => void;
};

export const ProductsLinks = ({ onProductSelect }: ProductsLinksProps) => {
  const navigate = useNavigate();
  const { currentChurch } = useGlobalSettings();
  const { isImpersonating } = useImpersonation();
  const isLimitedClusterSubView = useIsLimitedClusterSubView();
  const { mutate: setAdminProductContext } = useSetAdminProductContextMutation();
  const isLimitedScope = isLimitedAdminScope({
    isLimitedScope: currentChurch?.isLimitedScope,
    isGroupScope: currentChurch?.isGroupScope,
  });
  const canFetchChurchProducts =
    !isLimitedScope ||
    hasGroupScopeType({
      scopeTypes: currentChurch?.scopeTypes,
      isGroupScope: currentChurch?.isGroupScope,
    }) ||
    hasCommunityListScopeType({
      scopeTypes: currentChurch?.scopeTypes,
    });

  const {
    data: products = {},
    isFetched,
    isFetching,
  } = useQuery<Partial<ChurchProductsMap>>({
    queryKey: [QUERY_KEYS.CHURCH_PRODUCTS, currentChurch?.churchId],
    queryFn: () => churchesService.getChurchesProducts(currentChurch?.churchId || ''),
    enabled: Boolean(currentChurch?.churchId) && canFetchChurchProducts,
    retry: false,
  });
  const isProductsLoading = canFetchChurchProducts && isFetching && !isFetched;

  const enabledProducts = LINKS.filter((link) => {
    const productData = products[link.id];
    if (!productData?.isEnabled) {
      return false;
    }

    // Internal SPA products (Messages, Forms, etc.) may have an empty external url.
    if (getProductInternalRoute({ product: link.id })) {
      return true;
    }

    const productUrl = productData.url?.trim() ?? '';
    return Boolean(productUrl);
  });

  const handleProductLinkClick = ({
    event,
    product,
  }: {
    event: MouseEvent<HTMLAnchorElement>;
    product: CHURCH_PRODUCT;
  }) => {
    event.preventDefault();

    if (!isImpersonating) {
      setAdminProductContext({ product });
    }

    const internalPath = getProductInternalRoute({ product });
    if (internalPath) {
      onProductSelect?.();
      navigate(internalPath);
      return;
    }

    const url = products[product]?.url?.trim() ?? '';
    if (!url) {
      return;
    }

    onProductSelect?.();
    window.location.assign(url);
  };

  if (isLimitedClusterSubView || !canFetchChurchProducts) {
    return null;
  }

  if (isProductsLoading) {
    return (
      <section className={styles.wrapper}>
        {Array.from({ length: LOADING_SKELETON_COUNT }).map((_, index) => (
          <div className={styles.link} key={index}>
            <Skeleton height={48} width={48} radius="100%" />
            <Skeleton height={24} width="100%" />
          </div>
        ))}
      </section>
    );
  }

  return (
    <section className={styles.wrapper}>
      {enabledProducts.map((link) => (
        <a
          href={products[link.id]?.url || ''}
          key={link.label}
          className={styles.link}
          onClick={(event) => handleProductLinkClick({ event, product: link.id })}
        >
          <span className={styles.icon} style={{ backgroundColor: link.bg }}>
            <Image src={link.icon} alt={link.label} width={26} height={26} />
          </span>
          {link.label}
        </a>
      ))}
    </section>
  );
};
